import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Avvvatars from "avvvatars-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Upload, Palette, X } from "lucide-react";

interface AvatarPickerProps {
  userId: string;
  currentAvatarUrl: string | null;
  displayName: string;
  onAvatarChange: (url: string | null) => void;
}

const avatarSeeds = [
  "alpha", "beta", "gamma", "delta", "epsilon",
  "zeta", "eta", "theta", "iota", "kappa",
  "lambda", "mu", "sigma", "omega", "phoenix",
  "nova", "atlas", "orion", "vega", "lyra",
];

export default function AvatarPicker({
  userId,
  currentAvatarUrl,
  displayName,
  onAvatarChange,
}: AvatarPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"upload" | "generate">("generate");
  const [uploading, setUploading] = useState(false);
  const [selectedSeed, setSelectedSeed] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error(t("avatarPicker.onlyImages"));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("avatarPicker.maxFileSize"));
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${userId}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(path);

      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      await supabase.auth.updateUser({
        data: { avatar_url: publicUrl, avatar_type: "upload" },
      });

      onAvatarChange(publicUrl);
      toast.success(t("avatarPicker.uploaded"));
      setOpen(false);
    } catch (err: any) {
      toast.error(err.message || t("avatarPicker.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const handleSelectAvvvatar = async (seed: string) => {
    setSelectedSeed(seed);
    try {
      await supabase.auth.updateUser({
        data: { avatar_url: null, avatar_type: "avvvatar", avatar_seed: seed },
      });
      onAvatarChange(null);
      toast.success(t("avatarPicker.saved"));
      setOpen(false);
    } catch (err: any) {
      toast.error(err.message || t("avatarPicker.saveFailed"));
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="group relative w-16 h-16 rounded-full overflow-hidden shrink-0"
      >
        <AvatarDisplay
          avatarUrl={currentAvatarUrl}
          displayName={displayName}
          seed={selectedSeed}
          size={64}
        />
        <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/30 transition-colors flex items-center justify-center">
          <Palette className="w-5 h-5 text-background opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </button>

      {open && (
        <div className="absolute top-20 left-0 z-50 w-[400px] bg-card border border-border shadow-lg animate-fade-in">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="text-sm font-medium">{t("avatarPicker.changeAvatar")}</h3>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex border-b border-border">
            <button
              onClick={() => setTab("generate")}
              className={cn(
                "flex-1 py-2.5 text-sm transition-colors border-b-2",
                tab === "generate"
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {t("avatarPicker.selectAvatar")}
            </button>
            <button
              onClick={() => setTab("upload")}
              className={cn(
                "flex-1 py-2.5 text-sm transition-colors border-b-2",
                tab === "upload"
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {t("avatarPicker.uploadImage")}
            </button>
          </div>

          <div className="p-4">
            {tab === "generate" ? (
              <div className="grid grid-cols-5 gap-3">
                {avatarSeeds.map((seed) => (
                  <button
                    key={seed}
                    onClick={() => handleSelectAvvvatar(seed)}
                    className={cn(
                      "rounded-full p-1 transition-all hover:scale-110",
                      selectedSeed === seed && "ring-2 ring-accent ring-offset-2 ring-offset-card"
                    )}
                  >
                    <Avvvatars value={seed} style="character" size={48} />
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-border hover:border-muted-foreground p-8 flex flex-col items-center gap-3 cursor-pointer transition-colors"
                >
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {t("avatarPicker.dragOrClick")}
                  </p>
                  <p className="text-micro">{t("avatarPicker.maxSize")}</p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file);
                  }}
                />
                {uploading && (
                  <p className="text-sm text-center text-muted-foreground">{t("avatarPicker.uploading")}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Renders the correct avatar based on type */
export function AvatarDisplay({
  avatarUrl,
  displayName,
  seed,
  size = 40,
  className,
}: {
  avatarUrl: string | null;
  displayName: string;
  seed?: string | null;
  size?: number;
  className?: string;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={displayName}
        className={cn("rounded-full object-cover", className)}
        style={{ width: size, height: size }}
      />
    );
  }

  if (seed) {
    return <Avvvatars value={seed} style="character" size={size} />;
  }

  const initials = displayName.slice(0, 2).toUpperCase();
  return (
    <div
      className={cn(
        "rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium",
        className
      )}
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
}
